import type {
  PatientHistoryJobDerivedProvider,
  PatientHistoryJobDerivedStatus,
  PatientHistoryRequestModel,
} from '@ctw/shared/api/fhir/models/patient-history';
import { usePatientHistoryDetails } from '@ctw/shared/api/patient-history/patient-history';
import { usePatientSubscription } from '@ctw/shared/api/subscriptions/subscriptions';
import { Alert } from '@ctw/shared/components/alert';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { Link } from '@ctw/shared/components/link';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { ZusLogoIconOnly } from '@ctw/shared/components/zus-logo-icon-only';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { RequestRecordsButton } from '@ctw/shared/content/patient-history/request-records-button';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import {
  type IconDefinition,
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleExclamation,
  faCircleExclamationCheck,
  faCircleQuestion,
  faClock,
  faHourglassClock,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { OverviewCard } from '../../components/containers/overview-card';
import { usePatientHistory } from '../patient-history/use-patient-history';
import { SupportFormLink } from '../support-form-link';

export const PatientHistoryStatusCard = withOverviewCardErrorBoundary({
  cardTitle: 'Records Provided by Zus Health',
  boundaryName: 'PatientHistoryStatusCardComponent',
  Component: () => {
    const patientHistory = usePatientHistory();

    return (
      <OverviewCard
        fullbleed={true}
        headerIcon={<ZusLogoIconOnly width={25} height={25} />}
        title="Records Provided by Zus Health"
        loading={patientHistory.isLoading}
        testId="zus-information-card"
        empty={patientHistory.isError}
        emptyState="Unable to load records request information"
        className={tw`px-0`}
      >
        <div className={tw`flex h-full flex-col space-y-2`}>
          <div className={tw`flex-1`}>
            <PatientHistoryDetails />
          </div>
          <HorizontalDivider className={tw`!py-0 !my-0 !h-0`} />
          <div className={tw`flex flex-col gap-1 px-3 pt-1 pb-3 font-medium`}>
            <div className={tw`flex items-center justify-between`}>
              <div className={tw`font-mediumtext-sm/5 text-content-main`}>
                Have feedback or need help?
              </div>
              <SupportFormLink buttonText="Let us know" className={tw`block text-sm`} />
            </div>
            <div className={tw`flex items-center justify-between`}>
              <div className={tw`font-medium text-content-main text-sm/5`}>
                Learn more about the ZAP
              </div>
              <Link
                href="https://clinicalguide.zushealth.com/docs/getting-started"
                className={tw`block text-sm`}
                target="_blank"
              >
                Learn more about the ZAP
              </Link>
            </div>
          </div>
        </div>
      </OverviewCard>
    );
  },
});

const PatientHistoryDetails = () => {
  const patientHistoryDetails = usePatientHistoryDetails();
  const builderConfig = useBuilderConfig();

  if (patientHistoryDetails.isLoading) {
    return <LoadingSpinner message="Loading..." className={tw`w-full`} />;
  }

  if (patientHistoryDetails.isError) {
    return <Alert type="error" header="Error loading data" />;
  }

  const isInProgress = !!patientHistoryDetails.data?.inProgress;
  const job = isInProgress
    ? patientHistoryDetails.data?.inProgress
    : patientHistoryDetails.data?.completed;

  return (
    <div className={tw`@container grid grid-cols-1 gap-1 py-2 `}>
      {/* Top section: Package info and Request Records button */}
      <div className={tw`flex items-start px-3`}>
        <PatientSubscriptionPackage />
        <div className={tw`ml-auto`}>
          {builderConfig.showRequestRecords && <RequestRecordsButton />}
        </div>
      </div>

      {job ? (
        <div className={tw`flex w-full flex-col gap-2`}>
          <div className={tw`px-3`}>
            <div className={tw`text-content-subtle text-xs/4`}>{jobSyncStatusMessage({ job })}</div>
          </div>
          <HorizontalDivider className={tw`!py-0 !my-0 !h-0`} />
          <div className={tw`flex flex-col gap-2 px-3`}>
            {job?.ehrNetworkStatus && (
              <DataProviderStatus
                job={job}
                derivedStatus={job?.ehrNetworkStatus}
                name="EHR Data"
                subProviders={job?.derivedProviders.filter((provider) => provider.isEhrNetwork)}
              />
            )}

            {job?.derivedProviders
              .filter((provider) => !provider.isEhrNetwork)
              .map((provider) => (
                <DataProviderStatus
                  job={job}
                  key={provider.service}
                  derivedStatus={provider.derivedStatus}
                  name="Pharmacy Data"
                  subProviders={job?.derivedProviders.filter((provider) => !provider.isEhrNetwork)}
                />
              ))}
          </div>
        </div>
      ) : (
        <div>No patient history requests have been run for this patient.</div>
      )}
    </div>
  );
};

interface DataProviderStatusProps {
  derivedStatus: PatientHistoryJobDerivedStatus;
  name: string;
  job: PatientHistoryRequestModel;
  subProviders: Array<PatientHistoryJobDerivedProvider>;
}

const DataProviderStatus = ({
  derivedStatus,
  name,
  job,
  subProviders,
}: DataProviderStatusProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasErrors =
    ['failed', 'completeWithErrors'].includes(derivedStatus) ||
    subProviders.some((provider) =>
      ['failed', 'completeWithErrors'].includes(provider.derivedStatus),
    );

  return (
    <details
      className={tw`collapsible-content group/collapsible rounded border border-border-main bg-background shadow-sm`}
      open={hasErrors}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onToggle={(event) => {
        event.stopPropagation();
        setIsOpen(event.newState === 'open');
      }}
    >
      <summary
        className={twx(
          'flex cursor-pointer items-center justify-between gap-1 border-border-main px-2 py-2 hover:bg-background-hover',
          {
            'border-b': isOpen,
          },
        )}
      >
        <div className={tw`flex max-w-fit flex-row items-center gap-2`}>
          <ProviderStatusIcon derivedStatus={derivedStatus} subProviders={subProviders} />
          <div className={tw`font-medium text-content-main text-sm/5`}>{name}</div>
        </div>
        <div className={tw`flex max-w-fit flex-row items-center gap-2.5`}>
          <div
            className={twx(
              'text-xs/4',
              // Don't emphasize errors at the top-level; the sub-provider details will default to
              // open and emphasize the individual provider failures
              subProviders.some((provider) =>
                ['failed', 'completeWithErrors'].includes(provider.derivedStatus),
              )
                ? 'text-content-main'
                : colorForDerivedStatus({ derivedStatus, emphasizeErrors: false }),
            )}
          >
            {providerDerivedStatusMessage({ derivedStatus, subProviders })}
          </div>
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={tw`h-4 w-4 text-content-main`}
          />
        </div>
      </summary>

      <div
        className={tw`h-0 px-2 py-2 opacity-0 transition-all duration-300 group-open/collapsible:h-max group-open/collapsible:opacity-100`}
      >
        {subProviders && subProviders.length > 0 && (
          <div className={tw`flex flex-col gap-1 py-1`}>
            {subProviders.map((provider) => (
              <div key={provider.service} className={tw`flex items-center gap-1`}>
                <SubproviderStatusIcon derivedStatus={provider.derivedStatus} />
                <div
                  className={twx(
                    'text-xs',
                    colorForDerivedStatus({ derivedStatus: provider.derivedStatus }),
                  )}
                >
                  {subProviderDerivedStatusMessage({ job, provider })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  );
};

interface ColorForDerivedStatusArgs {
  derivedStatus: PatientHistoryJobDerivedStatus;
  emphasizeErrors?: boolean;
}

const colorForDerivedStatus = ({
  derivedStatus,
  emphasizeErrors = true,
}: ColorForDerivedStatusArgs): string => {
  switch (derivedStatus) {
    case 'failed':
    case 'completeWithErrors':
      return emphasizeErrors ? 'text-error-main' : 'text-content-main';
    default:
      return 'text-content-subtle';
  }
};

interface JobSyncStatusMessageArgs {
  job: PatientHistoryRequestModel;
}

const jobSyncStatusMessage = ({ job }: JobSyncStatusMessageArgs): string => {
  switch (job.derivedStatus) {
    case 'queued':
    case 'inProgress':
    case 'partiallyComplete':
      return 'Request in Progress...';
    default:
      return `Last synced ${job.lastUpdatedAt}`;
  }
};

interface ProviderDerivedStatusMessageArgs {
  derivedStatus: PatientHistoryJobDerivedStatus;
  subProviders: Array<PatientHistoryJobDerivedProvider>;
}

const providerDerivedStatusMessage = ({
  derivedStatus,
  subProviders,
}: ProviderDerivedStatusMessageArgs): string => {
  const providerCount = subProviders.length;
  const errorCount = subProviders.filter((provider) =>
    ['failed', 'completeWithErrors'].includes(provider.derivedStatus),
  ).length;

  // Regardless of top-level job status, return an error message if any providers have errors
  if (errorCount > 0) {
    return `Error fetching records (${errorCount}/${providerCount} Failed)`;
  }

  switch (derivedStatus) {
    case 'queued':
      return 'Queued';
    case 'inProgress':
    case 'partiallyComplete':
      // Show both in progress jobs and partially complete jobs as in progress
      return 'In Progress';
    case 'failed':
    case 'completeWithErrors':
      return `Error fetching records (${errorCount}/${providerCount} Failed)`;
    default:
      // For completed jobs, or unknown statuses, return no message
      return '';
  }
};

interface SubProviderDerivedStatusMessageArgs {
  job: PatientHistoryRequestModel;
  provider: PatientHistoryJobDerivedProvider;
}

const subProviderDerivedStatusMessage = ({
  job,
  provider,
}: SubProviderDerivedStatusMessageArgs): string => {
  switch (provider.derivedStatus) {
    case 'queued':
      return `${provider.serviceDisplayName} is queued`;
    case 'inProgress':
    case 'partiallyComplete':
      // Show both in progress jobs and partially complete jobs as in progress
      return `${provider.serviceDisplayName} in progress`;
    case 'complete':
      return `${provider.serviceDisplayName} completed on ${job.lastUpdatedAt ?? 'Unknown'}`;
    case 'completeWithErrors':
      return `${provider.serviceDisplayName} completed with errors on ${job.lastUpdatedAt ?? 'Unknown'}`;
    case 'failed':
      return `${provider.serviceDisplayName} failed at ${job.lastUpdatedAt ?? 'Unknown'}`;
    default:
      return provider.serviceDisplayName;
  }
};

interface ProviderStatusIconProps {
  derivedStatus: PatientHistoryJobDerivedStatus;
  subProviders: Array<PatientHistoryJobDerivedProvider>;
}

const ProviderStatusIcon = ({ derivedStatus, subProviders }: ProviderStatusIconProps) => {
  let statusIcon: IconDefinition;
  let statusLabel: string;
  let className = '';

  switch (derivedStatus) {
    case 'queued':
      statusIcon = faHourglassClock;
      statusLabel = 'Queued';
      break;
    case 'inProgress':
      statusIcon = faClock;
      statusLabel = 'In Progress';
      break;
    case 'complete':
    case 'partiallyComplete':
    case 'completeWithErrors':
    case 'failed': {
      // Terminal statuses should display a top-level status based on sub-provider status
      const hasFailedSubproviders = subProviders.some(
        (provider) => provider.derivedStatus === 'failed',
      );
      const hasCompleteWithErrorsSubproviders = subProviders.some(
        (provider) => provider.derivedStatus === 'completeWithErrors',
      );
      const hasPartiallyCompleteSubproviders = subProviders.some(
        (provider) => provider.derivedStatus === 'partiallyComplete',
      );

      if (hasFailedSubproviders) {
        statusIcon = faCircleExclamation;
        statusLabel = 'Error';
        break;
      }

      if (hasCompleteWithErrorsSubproviders) {
        statusIcon = faCircleExclamationCheck;
        statusLabel = 'Partially Complete';
        className = '-ml-0.5 w-5 h-5';
        break;
      }

      if (hasPartiallyCompleteSubproviders) {
        statusIcon = faClock;
        statusLabel = 'In Progress';
        break;
      }

      // otherwise, we're done!
      statusIcon = faCircleCheck;
      statusLabel = 'Done';
      break;
    }
    default:
      statusIcon = faCircleQuestion;
      statusLabel = derivedStatus;
      className = 'text-content-subtle';
  }

  return (
    <Tooltip
      id={`provider-status-tooltip-${derivedStatus}-${Math.random().toString(10)}`}
      content={statusLabel}
    >
      <FontAwesomeIcon
        icon={statusIcon}
        className={twx(
          'h-4 w-4 text-content-main',
          colorForDerivedStatus({ derivedStatus }),
          className,
        )}
      />
    </Tooltip>
  );
};

interface SubproviderStatusIconProps {
  derivedStatus: PatientHistoryJobDerivedStatus;
}

const SubproviderStatusIcon = ({ derivedStatus }: SubproviderStatusIconProps) => {
  let statusIcon: IconDefinition;
  let statusLabel: string;
  let className = '';

  switch (derivedStatus) {
    case 'complete':
      statusIcon = faCircleCheck;
      statusLabel = 'Done';
      break;
    case 'queued':
      statusIcon = faHourglassClock;
      statusLabel = 'Queued';
      break;
    case 'inProgress':
    case 'partiallyComplete':
      statusIcon = faClock;
      statusLabel = 'In Progress';
      break;
    case 'completeWithErrors':
      statusIcon = faCircleExclamationCheck;
      statusLabel = 'Partially Complete';
      className = '-ml-0.5 w-5 h-5';
      break;
    case 'failed':
      statusIcon = faCircleExclamation;
      statusLabel = 'Error';
      break;
    default:
      statusIcon = faCircleQuestion;
      statusLabel = derivedStatus;
      className = 'text-content-subtle';
  }

  return (
    <Tooltip
      id={`provider-status-tooltip-${derivedStatus}-${Math.random().toString(10)}`}
      content={statusLabel}
    >
      <FontAwesomeIcon
        icon={statusIcon}
        className={twx(
          'h-4 w-4 text-content-main',
          colorForDerivedStatus({ derivedStatus }),
          className,
        )}
      />
    </Tooltip>
  );
};

const PatientSubscriptionPackage = () => {
  const patientSubscription = usePatientSubscription();

  if (patientSubscription.isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (patientSubscription.isError) {
    return <Alert type="error" header="Error loading data" />;
  }

  const patientPackage = patientSubscription.data?.package;

  return (
    <div className={tw`font-semibold`}>
      {patientPackage ? `Enrolled in ${patientPackage.name}` : 'Not enrolled in package'}
    </div>
  );
};
